import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { DollarSign } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import '../styles/Builders.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface Project {
  _id: string;
  name: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface FinanceReportItem {
  unitId: string;
  unitNo: string;
  dealStatus: string;
  registered: boolean;
  unitStatus: string;
  submitted: boolean;
  colorCollection: string[];
  colorCollectionPrice: number;
  colorCollectionCost: number;
  package: string[];
  packagePrice: number;
  packageCost: number;
  personalizationOptions: string[];
  personalizationOptionsPrice: number;
  personalizationOptionsCost: number;
  subTotalPrice: number;
  subTotalCost: number;
  priceHST: number;
  costHST: number;
  totalPrice: number;
  totalCost: number;
  decorDollar: number;
  totalFinalPrice: number;
  netRevenueWithDecorDollar: number;
  netRevenueWithoutDecorDollar: number;
  totalRevenue: number;
  profit: number;
}

const ReportsFinance: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [financeData, setFinanceData] = useState<FinanceReportItem[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`);
      setProjects(response.data.projects || []);

      // Auto-select first project if available
      if (response.data.projects && response.data.projects.length > 0) {
        setSelectedProjectId(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const fetchFinanceReports = useCallback(async () => {
    if (!selectedProjectId || !workspaceId) return;

    try {
      setLoadingReports(true);
      const params: any = {
        projectId: selectedProjectId,
        page: pagination.page,
        limit: pagination.limit
      };

      if (selectedPhase) {
        params.phase = selectedPhase;
      }

      const response = await api.get(`/workspaces/${workspaceId}/finance-reports`, {
        params
      });
      setFinanceData(response.data.financeData || []);
      setPagination(response.data.pagination);
    } catch (err: any) {
      console.error('Error fetching finance reports:', err);
      toast.error('Failed to load finance reports');
    } finally {
      setLoadingReports(false);
    }
  }, [workspaceId, selectedProjectId, selectedPhase, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchFinanceReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProjectId, selectedPhase, pagination.page, pagination.limit]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProjectId(e.target.value);
    setSelectedPhase('');
    setPagination({ ...pagination, page: 1 });
  };

  const handlePhaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPhase(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 });
  };

  const handleExport = async () => {
    if (!selectedProjectId || !workspaceId) return;

    try {
      setExporting(true);
      const params: any = {
        projectId: selectedProjectId,
        export: true
      };

      if (selectedPhase) {
        params.phase = selectedPhase;
      }

      const response = await api.get(`/workspaces/${workspaceId}/finance-reports`, {
        params,
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const phaseName = selectedPhase ? `_${selectedPhase.replace(/\s+/g, '_')}` : '';
      link.setAttribute('download', `finance_report${phaseName}_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (err: any) {
      console.error('Error exporting finance report:', err);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatBoolean = (value: boolean) => {
    return value ? 'Yes' : 'No';
  };

  const formatArrayItems = (items: string[]) => {
    if (!items || items.length === 0) return '';
    return items.map((item, index) => (
      <span key={index}>
        {item}{index < items.length - 1 ? ',' : ''}
        {index < items.length - 1 && <br />}
      </span>
    ));
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (projects.length === 0) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Finance Reports</h1>
              <p className="builders-section-subtitle">
                Comprehensive financial data for all units
              </p>
            </div>
          </div>
          <div className="builders-empty-state">
            <DollarSign size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>No projects found. Please create a project first.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="builders-container">
        {/* Page Header with Project Dropdown */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Finance Reports</h1>
            <p className="builders-section-subtitle">
              Comprehensive financial data for all units
            </p>
          </div>
          <div style={{ marginTop: '4px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              className="form-input"
              value={selectedProjectId}
              onChange={handleProjectChange}
              disabled={loading || projects.length === 0}
              style={{ minWidth: '280px' }}
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
            <select
              className="form-input"
              value={selectedPhase}
              onChange={handlePhaseChange}
              disabled={!selectedProjectId}
              style={{ minWidth: '200px' }}
            >
              <option value="">All Phases</option>
              <option value="One">Phase One</option>
              <option value="Two">Phase Two</option>
              <option value="Three">Phase Three</option>
              <option value="Four">Phase Four</option>
              <option value="Five">Phase Five</option>
            </select>
            <button
              className="btn btn-primary btn-md"
              onClick={handleExport}
              disabled={!selectedProjectId || exporting || loadingReports}
              style={{ whiteSpace: 'nowrap' }}
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        {loadingReports ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : financeData.length > 0 ? (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Table wrapper with horizontal scroll */}
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div className="data-table-container" style={{ minWidth: '3500px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Unit No</th>
                      <th>Deal Status</th>
                      <th style={{ textAlign: 'center' }}>Registered</th>
                      <th>Unit Status</th>
                      <th style={{ textAlign: 'center' }}>Submitted</th>
                      <th>Color Collection</th>
                      <th style={{ textAlign: 'right' }}>Color Collection Price</th>
                      <th style={{ textAlign: 'right' }}>Color Collection Cost</th>
                      <th>Package</th>
                      <th style={{ textAlign: 'right' }}>Package Price</th>
                      <th style={{ textAlign: 'right' }}>Package Cost</th>
                      <th>Personalization Options</th>
                      <th style={{ textAlign: 'right' }}>Personalization Options Price</th>
                      <th style={{ textAlign: 'right' }}>Personalization Options Cost</th>
                      <th style={{ textAlign: 'right' }}>Sub Total Price</th>
                      <th style={{ textAlign: 'right' }}>Sub Total Cost</th>
                      <th style={{ textAlign: 'right' }}>Price - HST</th>
                      <th style={{ textAlign: 'right' }}>Cost - HST</th>
                      <th style={{ textAlign: 'right' }}>Total Price</th>
                      <th style={{ textAlign: 'right' }}>Total Cost</th>
                      <th style={{ textAlign: 'right' }}>Decor Dollar</th>
                      <th style={{ textAlign: 'right' }}>Total Final Price</th>
                      <th style={{ textAlign: 'right' }}>Net Revenue - With Decor Dollar</th>
                      <th style={{ textAlign: 'right' }}>Net Revenue - Without Decor Dollar</th>
                      <th style={{ textAlign: 'right' }}>Total Revenue</th>
                      <th style={{ textAlign: 'right' }}>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financeData.map((item) => (
                      <tr key={item.unitId}>
                        <td className="data-table-primary-cell">{item.unitNo}</td>
                        <td>{item.dealStatus}</td>
                        <td style={{ textAlign: 'center' }}>{formatBoolean(item.registered)}</td>
                        <td>{item.unitStatus}</td>
                        <td style={{ textAlign: 'center' }}>{formatBoolean(item.submitted)}</td>
                        <td>{formatArrayItems(item.colorCollection)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.colorCollectionPrice)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.colorCollectionCost)}</td>
                        <td>{formatArrayItems(item.package)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.packagePrice)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.packageCost)}</td>
                        <td>{formatArrayItems(item.personalizationOptions)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.personalizationOptionsPrice)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.personalizationOptionsCost)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.subTotalPrice)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.subTotalCost)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.priceHST)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.costHST)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.totalPrice)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.totalCost)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.decorDollar)}</td>
                        <td style={{ textAlign: 'right' }} className="data-table-primary-cell">{formatCurrency(item.totalFinalPrice)}</td>
                        <td style={{ textAlign: 'right' }} className="data-table-primary-cell">{formatCurrency(item.netRevenueWithDecorDollar)}</td>
                        <td style={{ textAlign: 'right' }} className="data-table-primary-cell">{formatCurrency(item.netRevenueWithoutDecorDollar)}</td>
                        <td style={{ textAlign: 'right' }} className="data-table-primary-cell">{formatCurrency(item.totalRevenue)}</td>
                        <td style={{ textAlign: 'right' }} className="data-table-primary-cell">{formatCurrency(item.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination and Records Per Page */}
            <div className="contacts-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="contacts-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="contacts-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="builders-empty-state">
            <DollarSign size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>No financial data available for this project</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ReportsFinance;
